/*============================================================================
  GA4 ecommerce dataLayer -> GTM (GTM-WCHDNGJJ)

  Server-rendered context (currency, the cart at page load, and view_item /
  view_cart) is pushed by snippets/ga4-datalayer.liquid before the GTM
  container loads. This file covers everything that happens after first paint.

  add_to_cart and remove_from_cart are derived by diffing /cart.js against a
  snapshot rather than read off event payloads, because:
    - `cart:updated` is not dispatched on add-to-cart (theme.js buildCart()
      renders markup without it, which is why cart-drawer.liquid re-fetches
      /cart.js on its own), and
    - three of the `ajaxProduct:added` dispatch sites send no detail at all
      (theme.js:2797, main-collection.liquid:637).
  Diffing gives one correct path for every add/remove route (PDP form, quick
  shop, drawer +/- buttons, cart page, in-cart upsell) and is idempotent, so
  overlapping listeners cannot double-count.
==============================================================================*/

(function() {
  'use strict';

  var config = window.LR_GA4 || (window.LR_GA4 = {});
  var fallbackCurrency = config.currency || 'USD';

  window.dataLayer = window.dataLayer || [];

  // Line items keyed by cart line key: { key: { quantity: n, line: {...} } }
  var lastLines = snapshot(config.cart || { items: [] });
  var refreshTimer = null;

  /*========================================================================
    Helpers
  ==========================================================================*/

  // GA4 wants a clean ecommerce object per event. Nulling it first stops the
  // previous event's items[] leaking into this one via the dataLayer model.
  function push(eventName, ecommerce) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: eventName, ecommerce: ecommerce });
  }

  function money(cents) {
    return Math.round(cents) / 100;
  }

  function sumValue(items) {
    var total = items.reduce(function(sum, item) {
      return sum + (item.price * (item.quantity || 1));
    }, 0);
    return Math.round(total * 100) / 100;
  }

  // Map a /cart.js line item onto the GA4 recommended items[] schema.
  // item_id is the SKU where one exists, falling back to variant id so the
  // field is never empty.
  function lineToItem(line) {
    var item = {
      item_id: line.sku || String(line.variant_id),
      item_name: line.product_title || line.title,
      price: money(line.final_price != null ? line.final_price : line.price),
      quantity: line.quantity
    };

    if (line.vendor) item.item_brand = line.vendor;
    if (line.product_type) item.item_category = line.product_type;
    // Shopify sends "Default Title" for single-variant products; that is noise.
    if (line.variant_title && line.variant_title !== 'Default Title') {
      item.item_variant = line.variant_title;
    }

    return item;
  }

  function snapshot(cart) {
    var map = {};
    (cart.items || []).forEach(function(line) {
      map[line.key] = { quantity: line.quantity, line: line };
    });
    return map;
  }

  function currentItems() {
    return Object.keys(lastLines).map(function(key) {
      return lineToItem(lastLines[key].line);
    });
  }

  /*========================================================================
    add_to_cart / remove_from_cart via cart diffing
  ==========================================================================*/

  function diff(cart) {
    var next = snapshot(cart);
    var currency = cart.currency || fallbackCurrency;
    var added = [];
    var removed = [];
    var key;

    for (key in next) {
      if (!Object.prototype.hasOwnProperty.call(next, key)) continue;

      var before = lastLines[key] ? lastLines[key].quantity : 0;
      var delta = next[key].quantity - before;

      if (delta === 0) continue;

      var item = lineToItem(next[key].line);
      item.quantity = Math.abs(delta);
      (delta > 0 ? added : removed).push(item);
    }

    // Lines that disappeared entirely between snapshots.
    for (key in lastLines) {
      if (!Object.prototype.hasOwnProperty.call(lastLines, key)) continue;
      if (!next[key]) removed.push(lineToItem(lastLines[key].line));
    }

    lastLines = next;

    if (added.length) {
      push('add_to_cart', { currency: currency, value: sumValue(added), items: added });
    }
    if (removed.length) {
      push('remove_from_cart', { currency: currency, value: sumValue(removed), items: removed });
    }
  }

  // Always re-read /cart.js rather than trusting a cart handed to us by an
  // event. Debouncing collapses overlapping dispatches (ajaxProduct:added and
  // cart:updated often both fire for one user action) into a single fresh read,
  // which avoids a stale response racing a newer one and inventing a phantom
  // remove_from_cart.
  function refresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function() {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function(response) { return response.json(); })
        .then(diff)
        .catch(function() {});
    }, 150);
  }

  document.addEventListener('ajaxProduct:added', refresh);
  document.addEventListener('cart:updated', refresh);
  document.addEventListener('cart:upsell-updated', refresh);

  /*========================================================================
    begin_checkout
  ==========================================================================*/

  // Built from the last known cart so the push is synchronous — a fetch here
  // would race the navigation to /checkout and frequently lose.
  document.addEventListener('click', function(evt) {
    if (!evt.target.closest) return;
    if (!evt.target.closest('[name="checkout"]')) return;

    var items = currentItems();
    if (!items.length) return;

    push('begin_checkout', {
      currency: fallbackCurrency,
      value: sumValue(items),
      items: items
    });
  });

  /*========================================================================
    view_item_list / select_item

    Both read from the rendered product cards rather than from Liquid, so the
    payload always matches what is actually on screen. This covers every
    surface that renders product-grid-item.liquid — collection grids, search
    results, recommendations, featured collections, in-cart upsell — without
    having to touch each of the ~14 render sites.
  ==========================================================================*/

  var CARD_SELECTOR = '[data-ga4-item]';

  // The recommendation sections render a hidden, aria-hidden copy of the
  // current product as a layout placeholder before the real recommendations
  // load (product-recommendations.liquid, product-complementary.liquid).
  // Counting those would report phantom impressions of the product the
  // shopper is already looking at.
  function isRealCard(card) {
    return !card.closest('.product-recommendations-placeholder') &&
           !card.closest('[aria-hidden="true"]') &&
           !card.closest('.visually-invisible');
  }

  function allCards(root) {
    return Array.prototype.filter.call(
      (root || document).querySelectorAll(CARD_SELECTOR),
      isRealCard
    );
  }

  function cardToItem(card, index) {
    var item = {
      item_id: card.dataset.ga4Id,
      item_name: card.dataset.ga4Name,
      price: parseFloat(card.dataset.ga4Price) || 0,
      index: index + 1
    };

    if (card.dataset.ga4Brand) item.item_brand = card.dataset.ga4Brand;
    if (card.dataset.ga4Category) item.item_category = card.dataset.ga4Category;
    if (card.dataset.ga4Variant) item.item_variant = card.dataset.ga4Variant;

    return item;
  }

  function listNameFor(card) {
    var container = card.closest('[data-ga4-list]');
    return (container && container.dataset.ga4List) || config.defaultList || 'Product list';
  }

  // Group the cards on the page by the list they belong to.
  function collectLists() {
    var lists = {};

    allCards().forEach(function(card) {
      var name = listNameFor(card);
      (lists[name] = lists[name] || []).push(card);
    });

    return lists;
  }

  var seenLists = {};

  function pushListView(name, cards) {
    if (seenLists[name]) return;
    seenLists[name] = true;

    var items = cards.map(function(card, index) {
      var item = cardToItem(card, index);
      item.item_list_name = name;
      return item;
    });

    if (!items.length) return;

    push('view_item_list', {
      item_list_name: name,
      currency: fallbackCurrency,
      items: items
    });
  }

  // Fire a list only once it is actually scrolled into view, so a long
  // collection page does not report every list as seen on load.
  function observeLists() {
    var lists = collectLists();

    if (!('IntersectionObserver' in window)) {
      Object.keys(lists).forEach(function(name) { pushListView(name, lists[name]); });
      return;
    }

    Object.keys(lists).forEach(function(name) {
      var cards = lists[name];
      if (seenLists[name]) return;

      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          pushListView(name, cards);
          observer.disconnect();
        });
      }, { threshold: 0.3 });

      observer.observe(cards[0]);
    });
  }

  document.addEventListener('click', function(evt) {
    if (!evt.target.closest) return;

    var card = evt.target.closest(CARD_SELECTOR);
    if (!card || !isRealCard(card)) return;
    if (!evt.target.closest('a')) return;

    var name = listNameFor(card);
    var siblings = allCards(card.closest('[data-ga4-list]'));
    var item = cardToItem(card, Math.max(siblings.indexOf(card), 0));
    item.item_list_name = name;

    push('select_item', {
      item_list_name: name,
      currency: fallbackCurrency,
      items: [item]
    });
  });

  /*========================================================================
    Init
  ==========================================================================*/

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeLists);
  } else {
    observeLists();
  }

  // Grids that load in after paint (pagination, filtering, recommendations,
  // the in-cart upsell) bring new cards with them.
  ['collection:reloaded', 'recommendations:loaded', 'cart:upsell-updated'].forEach(function(name) {
    document.addEventListener(name, observeLists);
  });
})();
